import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  // Mechanical: no float/bounce. Press = translateY(1px) (§4.5, §5.1). One primary per view.
  primary:
    'bg-accent text-ink hover:bg-accent-hover active:bg-accent-press active:translate-y-px disabled:bg-surface-3 disabled:text-disabled disabled:translate-y-0',
  secondary:
    'bg-transparent text-accent border border-accent hover:bg-accent-tint active:bg-accent-tint active:border-accent-press active:text-accent-press disabled:border-border-strong disabled:text-disabled',
  ghost:
    'bg-transparent text-secondary hover:bg-surface-3 hover:text-primary active:bg-surface-3 disabled:text-disabled disabled:bg-transparent',
  destructive:
    'bg-transparent text-danger border border-danger hover:bg-danger-tint active:bg-danger active:text-ink disabled:border-border-strong disabled:text-disabled',
};

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-[12px]',
  md: 'h-10 px-4 text-[13px]',
  lg: 'h-12 px-5 text-[14px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'lg', block, loading, iconLeft, iconRight, className = '', children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-2 rounded font-mono font-bold uppercase tracking-[0.04em]',
        'select-none transition-colors duration-fast ease-machine focus-ring',
        'disabled:cursor-not-allowed',
        VARIANT[variant],
        SIZE[size],
        block ? 'w-full' : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {iconLeft}
      <span className="truncate">{children}</span>
      {iconRight}
    </button>
  );
});
